import { useState } from "react";
import { loadPrizes, isTrophy, isCoin, COIN_VALUE, exchangeForCoin } from "./prizes.js";
import { syncScore } from "./board.js";
import { t } from "./i18n.js";
import CoinImg from "./CoinImg.jsx";

/* ==================== BAMSEBØRSEN – tøm hylderne ============================
   Byt COIN_VALUE tøjdyr til 1 guldmønt med logobamsen. Mønten tæller som
   COIN_VALUE præmier på leaderboardet, så totalen er UÆNDRET — børsen giver
   luft på hylderne, ikke point. Udvælgelsen er automatisk: mindste størrelse
   først, ældste først (se prizes.exchangeForCoin). Pokaler er fredet.
============================================================================ */

export default function Exchange({ onExit }) {
  const [confirm, setConfirm] = useState(false);
  const [done, setDone] = useState(false);
  const [, bump] = useState(0);           // gen-render efter et byt

  const all = loadPrizes();
  const plush = all.filter((p) => !isTrophy(p) && !isCoin(p)).length;
  const coins = all.filter(isCoin).length;
  const missing = Math.max(0, COIN_VALUE - plush);
  const frac = Math.min(1, plush / COIN_VALUE);

  const trade = () => {
    const coin = exchangeForCoin(t("tile_exchange"));
    setConfirm(false);
    if (coin) {
      setDone(true);
      syncScore();               // totalen er uændret, men plush/coins skifter
      bump((x) => x + 1);
    }
  };

  return (
    <div className="ex-root">
      <style>{css}</style>
      <header className="ex-head">
        <button className="ex-back" onClick={onExit}>{t("back")}</button>
        <h1>{t("tile_exchange")}</h1>
        <span className="ex-count">{coins} {coins === 1 ? t("ex_coin_one") : t("ex_coin_many")}</span>
      </header>

      <div className="ex-booth">
        <div className="ex-coinrow">
          <CoinImg px={110} className="ex-coin" />
        </div>
        <p className="ex-lede">{t("ex_lede", { n: COIN_VALUE })}</p>

        <div className="ex-status">
          <div className="ex-bar">
            <div className="ex-fill" style={{ width: (frac * 100).toFixed(1) + "%" }} />
            <span className="ex-barlabel">{plush} / {COIN_VALUE}</span>
          </div>
          <p className="ex-line">
            {missing > 0 ? t("ex_missing", { n: missing }) : t("ex_ready")}
          </p>
        </div>

        <button className="ex-cta" disabled={missing > 0} onClick={() => setConfirm(true)}>
          {t("ex_cta", { n: COIN_VALUE })}
        </button>
        <p className="ex-note">{t("ex_note", { n: COIN_VALUE })}</p>
      </div>

      {confirm && (
        <div className="ex-overlay">
          <div className="ex-card">
            <div className="ex-ribbon">{t("ex_confirm_t")}</div>
            <p className="ex-cardline">{t("ex_confirm", { n: COIN_VALUE })}</p>
            <button className="ex-cta" onClick={trade}>{t("ex_yes")}</button>
            <button className="ex-cta alt" onClick={() => setConfirm(false)}>{t("ex_no")}</button>
          </div>
        </div>
      )}

      {done && (
        <div className="ex-overlay">
          <div className="ex-card">
            <div className="ex-ribbon gold">{t("ex_done_t")}</div>
            <div className="ex-coinrow"><CoinImg px={96} className="ex-coin spin" /></div>
            <p className="ex-cardline">{t("ex_done")}</p>
            <button className="ex-cta" onClick={() => setDone(false)}>{t("ex_more")}</button>
            <button className="ex-cta alt" onClick={onExit}>{t("to_menu")}</button>
          </div>
        </div>
      )}
    </div>
  );
}

const css = `
.ex-root{
  min-height:100vh; background:#1d3318;
  background-image:
    radial-gradient(circle at 50% -10%, rgba(120,190,90,.22) 0%, rgba(0,0,0,0) 55%),
    repeating-linear-gradient(45deg, rgba(214,178,94,.05) 0 2px, transparent 2px 14px),
    repeating-linear-gradient(-45deg, rgba(214,178,94,.05) 0 2px, transparent 2px 14px),
    linear-gradient(180deg,#24401f,#14230d);
  font-family:'Nunito',system-ui,sans-serif; color:#F6EED9;
  display:flex; flex-direction:column; align-items:center; padding-bottom:28px;
}
.ex-head{
  width:min(96%,520px); display:flex; align-items:center; justify-content:space-between;
  gap:8px; padding:14px 4px 8px;
}
.ex-head h1{
  font-family:'Alfa Slab One',Georgia,serif; font-size:clamp(17px,5vw,28px);
  margin:0; letter-spacing:2px;
  background:linear-gradient(180deg,#f7e08a 18%,#d4a943 55%,#9a6c1c 100%);
  -webkit-background-clip:text; background-clip:text; color:transparent;
  filter:drop-shadow(0 2px 1px rgba(0,0,0,.55));
}
.ex-back{
  background:linear-gradient(#6b452a,#4a2c16); color:#f2e7c9;
  border:1px solid #c9a24b; border-radius:9px;
  font-weight:800; font-size:13px; padding:7px 11px; cursor:pointer;
  box-shadow:0 3px 0 #2c1a0e, inset 0 1px 0 rgba(255,255,255,.15);
  white-space:nowrap; flex-shrink:0;
}
.ex-count{
  font-weight:800; font-size:12px; color:#f2dc9a;
  background:linear-gradient(#2e4a26,#1d3318);
  border:2px solid #c9a24b; border-radius:10px; padding:4px 10px;
  box-shadow:0 2px 5px rgba(0,0,0,.4), inset 0 1px 0 rgba(255,255,255,.12);
  white-space:nowrap; flex-shrink:0;
}
.ex-booth{
  width:min(96%,520px); text-align:center;
  background:#4a2f1c;
  background-image:
    radial-gradient(ellipse at 50% 0%, rgba(255,190,90,.20) 0%, rgba(255,190,90,0) 55%),
    linear-gradient(180deg,#553622,#3f2412);
  border:6px solid #2c1a0e; border-radius:18px; margin-top:4px;
  padding:22px 18px 20px;
  box-shadow:inset 0 0 0 3px #7a5230, inset 0 0 46px rgba(0,0,0,.5), 0 14px 34px rgba(0,0,0,.5);
}
.ex-coinrow{display:flex; justify-content:center; margin:0 0 12px;}
.ex-coin{filter:drop-shadow(0 0 14px rgba(255,205,80,.55)) drop-shadow(0 4px 5px rgba(0,0,0,.45));}
.ex-coin.spin{animation:ex-spin 2.4s ease-in-out infinite;}
@keyframes ex-spin{
  0%,100%{transform:rotateY(0deg);}
  50%{transform:rotateY(180deg);}
}
.ex-lede{font-size:14.5px; font-weight:700; line-height:1.5; margin:0 auto 16px; max-width:400px; color:#f2e7c9;}
.ex-status{margin:0 auto 6px; max-width:380px;}
.ex-bar{
  position:relative; height:26px; border-radius:14px; overflow:hidden;
  background:rgba(0,0,0,.4); border:2px solid #c9a24b;
}
.ex-fill{
  height:100%; border-radius:12px 0 0 12px;
  background:linear-gradient(180deg,#f7df9a,#e9b94e 55%,#c98f2a);
  transition:width .4s ease;
}
.ex-barlabel{
  position:absolute; inset:0; display:flex; align-items:center; justify-content:center;
  font-weight:800; font-size:13px; color:#fff; text-shadow:0 1px 3px rgba(0,0,0,.7);
}
.ex-line{font-weight:800; font-size:13px; margin:8px 0 0; color:#e9dfc0;}
.ex-cta{
  width:min(100%,380px); margin-top:14px; padding:14px; font-size:17px; font-weight:800; cursor:pointer;
  font-family:'Alfa Slab One',Georgia,serif; letter-spacing:.5px;
  background:linear-gradient(180deg,#f7df9a 0%,#e9b94e 48%,#c98f2a 100%);
  color:#4a2c00; text-shadow:0 1px 0 rgba(255,255,255,.45);
  border:4px solid #4a2c16; border-radius:12px;
  box-shadow:0 4px 0 #2e1a0c, inset 0 2px 3px rgba(255,255,255,.5), inset 0 -3px 5px rgba(0,0,0,.25);
}
.ex-cta:active{transform:translateY(3px); box-shadow:0 1px 0 #2e1a0c, inset 0 2px 3px rgba(255,255,255,.5), inset 0 -3px 5px rgba(0,0,0,.25);}
.ex-cta:disabled{filter:saturate(.35) brightness(.75); cursor:default; transform:none;}
.ex-cta.alt{
  background:#6b4a2e; color:#f2e7c9; border-color:#3f2a16; text-shadow:none;
  box-shadow:0 4px 0 #241708; font-size:14px; padding:11px; margin-top:10px;
}
.ex-cta.alt:active{box-shadow:0 1px 0 #241708;}
.ex-note{font-size:12px; font-weight:800; opacity:.7; margin:12px 0 0;}
.ex-overlay{
  position:fixed; inset:0; background:rgba(10,6,3,.78); z-index:10;
  display:flex; align-items:center; justify-content:center;
}
.ex-card{
  width:min(88%,380px); background:#F6EED9; color:#22301c;
  border-radius:14px; padding:0 22px 22px; text-align:center;
  box-shadow:0 18px 50px rgba(0,0,0,.6);
}
.ex-card .ex-coinrow{margin:14px 0 4px;}
.ex-ribbon{
  font-family:'Alfa Slab One',Georgia,serif; font-size:20px; letter-spacing:1px;
  background:#C8352E; color:#fff; margin:0 -22px 14px; padding:14px;
  border-radius:14px 14px 0 0;
}
.ex-ribbon.gold{background:linear-gradient(180deg,#e9b94e,#b8912e); color:#3c2c07;}
.ex-cardline{font-size:15px; font-weight:800; margin:6px 0 12px; line-height:1.5;}
`;
